import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { db } from '../services/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

interface BlogPost {
    id: string;
    title: string;
    excerpt: string;
    content: string;
    author: string;
    date: string;
    category: string;
    tags: string[];
    image?: string;
}

function calculateReadingTime(content: string) {
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]+>/g, '').split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
}

const ArticlesPage: React.FC = () => {
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const q = query(collection(db, 'blogs'), orderBy('date', 'desc'));
                const snapshot = await getDocs(q);
                const data = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as BlogPost[];
                setPosts(data);
            } catch (err) {
                console.error('Failed to fetch posts:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchPosts();
    }, []);

    // Get unique categories
    const categories = Array.from(new Set(posts.map((p) => p.category).filter(Boolean)));

    if (loading) {
        return (
            <div className="mx-auto max-w-3xl md:border-x px-4 py-24">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground" />
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-3xl md:border-x">
            <div className="px-4 py-12">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <h1 className="text-3xl font-bold text-foreground mb-2">Articles</h1>
                    <p className="text-sm text-muted-foreground mb-8">
                        Thoughts on ML, data science, and engineering.
                    </p>
                </motion.div>

                {/* Category Tags */}
                {categories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8">
                        {categories.map((cat) => (
                            <span
                                key={cat}
                                className="px-2 py-0.5 text-xs font-mono text-muted-foreground border border-border rounded"
                            >
                                #{cat}
                            </span>
                        ))}
                    </div>
                )}

                {/* Article List */}
                <div>
                    {posts.map((post, i) => (
                        <motion.div
                            key={post.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: i * 0.05 }}
                            onClick={() => {
                                navigate(`/blog/${post.id}`);
                                window.scrollTo(0, 0);
                            }}
                            className="group py-5 screen-line-after cursor-pointer"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 space-y-1.5">
                                    <h3 className="text-base font-semibold text-foreground group-hover:opacity-80 transition-opacity">
                                        {post.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground line-clamp-2">
                                        {post.excerpt}
                                    </p>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                                        <span>
                                            {new Date(post.date).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                            })}
                                        </span>
                                        <span>·</span>
                                        <span>{calculateReadingTime(post.content)} min read</span>
                                        {post.tags && post.tags.length > 0 && (
                                            <>
                                                <span>·</span>
                                                <div className="flex gap-1">
                                                    {post.tags.slice(0, 2).map((tag) => (
                                                        <span key={tag} className="font-mono">
                                                            #{tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <ArrowRight
                                    size={14}
                                    className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1"
                                />
                            </div>
                        </motion.div>
                    ))}
                </div>

                {posts.length === 0 && (
                    <p className="text-sm text-muted-foreground py-12 text-center">
                        No articles yet. Add some from the admin panel.
                    </p>
                )}
            </div>
        </div>
    );
};

export default ArticlesPage;
